import { Component, Suspense } from 'react'
import { connect } from 'react-redux'
import { isGroupUser } from 'utils'
import { getNominative } from '../../common/utils'
import useFeatureFlags from '../../hooks/useFeatureFlags'
import { history } from '../../store'
import Field from '../fields'
import Modal from '.'
import PopupModal from './PopupModal'

interface AddTeamMemberModalProps {
  createItem: (data: any) => void
  setFormField: (data: { key: string; value: any }) => void
  fetchList: (list: string) => void
  showToast: (options: { title: string }) => void
  fields: any // Replace 'any' with a specific type if available
  isOpen: boolean
  preferredPeopleNominative: string
  role: string | null
  features: any // Replace 'any' with a specific type if available
  managementRoleEnabled?: boolean
  auditViewRoleEnabled?: boolean
  itSupportRoleEnabled?: boolean
}

/**
 * @deprecated Replaced by `AddTeamMemberWizard` for the V2 team list
 * (gated by feature flag `roles_permissions_v2`). Still used by
 * TeamListLegacy, TeamListGroups, and Home/components/CarersCard.tsx;
 * delete once the v2 flag is GA. See CM-3382.
 */
export class AddTeamMemberModal extends Component<AddTeamMemberModalProps> {
  handleSubmit = () => {
    // @ts-ignore
    const { createItem, setFormField, user } = this.props
    setFormField({
      key: 'home',
      value: user.home
    })
    createItem({
      url: 'list/carers',
      onComplete: this.handleComplete
    })
  }

  handleComplete = (user: any) => {
    const { fetchList, showToast } = this.props
    fetchList('carers')
    showToast({ title: 'Added a Team Member' })
    history.push(`/organisation/team/${user.id}/profile`)
  }

  render() {
    const {
      fields,
      isOpen,
      preferredPeopleNominative,
      role,
      features,
      managementRoleEnabled,
      auditViewRoleEnabled,
      itSupportRoleEnabled,
      ...formProps
    } = this.props
    if (!fields || !isOpen) return null

    const isSeniorCarer = role === 'senior-carer'

    const carehomeOptions: Array<{
      label: string
      value: string
      disabled?: boolean
    }> = [...ROLE_OPTIONS_CAREHOME]
    if (managementRoleEnabled) {
      carehomeOptions.push(
        isSeniorCarer
          ? { label: 'Management', value: 'management', disabled: true }
          : { label: 'Management', value: 'management' }
      )
    }
    carehomeOptions.push(
      role === 'management' || isSeniorCarer
        ? { ...ROLE_OPTIONS_CAREHOME_ADMIN, disabled: true }
        : ROLE_OPTIONS_CAREHOME_ADMIN
    )
    if (auditViewRoleEnabled) {
      carehomeOptions.push(
        isSeniorCarer
          ? { label: 'Audit View', value: 'audit-view', disabled: true }
          : { label: 'Audit View', value: 'audit-view' }
      )
    }
    if (itSupportRoleEnabled) {
      carehomeOptions.push(
        isSeniorCarer
          ? { label: 'IT', value: 'it-security', disabled: true }
          : { label: 'IT', value: 'it-security' }
      )
    }

    const ROLE_OPTIONS = isGroupUser(role)
      ? ROLE_OPTIONS_GROUP
      : carehomeOptions

    const getFieldNote = () => ROLE_FIELD_NOTE()

    return (
      <PopupModal
        confirmText="Invite Team Member"
        title="Invite a new Team Member"
        buttonIcon="MailOutlined"
        buttonColour="success"
        list="Team Member"
        includesRequired
        onConfirm={this.handleSubmit}
        type="create"
        {...formProps}
      >
        <div style={{ marginBottom: 20 }}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              formId={MODAL_ID}
              keys={['name.first', 'name.last']}
              label="Name"
              placeholders={['First', 'Last']}
              required
              type="multiText"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={{ marginBottom: 20 }}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              className="email-field"
              formId={MODAL_ID}
              inputType="email"
              keys="email"
              label="Email"
              note={EMAIL_FIELD_NOTE}
              required
              type="text"
              validationTypes="email"
              values={fields}
            />
          </Suspense>
        </div>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            className="role-field"
            formId={MODAL_ID}
            keys="role"
            label="Role"
            note={
              role === 'group-admin' ? ROLE_FIELD_NOTE_GROUP : getFieldNote()
            }
            supportLink={SUPPORT_LINK}
            options={ROLE_OPTIONS}
            required
            type="radios"
            values={fields}
          />
        </Suspense>
      </PopupModal>
    )
  }
}

const ROLE_OPTIONS_CAREHOME = [
  { label: 'Carer', value: 'carer' },
  { label: 'Senior Carer', value: 'senior-carer' }
]

const ROLE_OPTIONS_CAREHOME_ADMIN = { label: 'Admin', value: 'carehome-admin' }

const ROLE_OPTIONS_GROUP = [
  { label: 'Group Admin', value: 'group-admin' },
  { label: 'Group Manager', value: 'group-manager' }
]

const MODAL_ID = 'addTeamMember'

const EMAIL_FIELD_NOTE = "This is where they'll get sent their login details."

const LEGACY_ROLE_FIELD_NOTE = (preferredPeopleNominative: string) =>
  `Carers can only access the Carer App, while Senior Carers also have limited access to the Care Office (viewing ${getNominative(
    preferredPeopleNominative,
    'Resident'
  )} and Report information only). Admins have full access to the Care Office. `

const ROLE_FIELD_NOTE = () =>
  `Carers can only access the Carer App
Senior Carers can access the app and limited Care Office access
Admins have full access to the Care Office and Carer App
Click on the blue question mark to find out more about these roles `

const ROLE_FIELD_NOTE_GROUP =
  'Group Admins are additionally able to invite and manage other team members.'
const SUPPORT_LINK =
  'https://support.logmycare.co.uk/en/articles/1541399-how-do-i-add-a-new-carer-senior-or-administrator'

const AddTeamMemberModalWithFlags = (props: AddTeamMemberModalProps) => {
  const flags = useFeatureFlags({
    flagNames: ['management_role', 'audit_view_role', 'it_role'] as const
  })?.data
  const managementRoleEnabled = flags?.management_role.enabled ?? false
  const auditViewRoleEnabled = flags?.audit_view_role.enabled ?? false
  const itSupportRoleEnabled = flags?.it_role.enabled ?? false

  return (
    <AddTeamMemberModal
      {...props}
      managementRoleEnabled={managementRoleEnabled}
      auditViewRoleEnabled={auditViewRoleEnabled}
      itSupportRoleEnabled={itSupportRoleEnabled}
    />
  )
}

const mapStateToProps = (state: any) => ({
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  role: state.global.user && state.global.user.role,
  features: state.global.home.features
})

// @ts-ignore
const wrappedModal = connect(mapStateToProps, null)(AddTeamMemberModalWithFlags)

export default Modal(wrappedModal, MODAL_ID)
